import { useState, useEffect, useCallback } from 'react';
import { Container, Button, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import Scanner from './components/Scanner';
import ItemList from './components/ItemList';
import Login from './components/Login';
import InstallationSelect from './components/InstallationSelect';
import { LynxProvider } from '@iotopen/react-lynx';
import { useGlobalSettings } from '@iotopen/lynx-react-components';
import {Devicex, Functionx} from '@iotopen/node-lynx';

interface Installation {
  id: number;
  name: string;
  description: string;
}

type Page = 'scanner' | 'list';

function App() {
    const [items, setItems] = useState<ScannedItem[]>([]);
    const [currentPage, setCurrentPage] = useState<Page>('list');
    const [token, setToken] = useState<string | null>(localStorage.getItem('auth_token'));
    const [installations, setInstallations] = useState<Installation[]>([]);
    const [isLoadingInstallations, setIsLoadingInstallations] = useState(false);
    const [selectedInstallation, setSelectedInstallation] = useState<number | null>(null);
    const [showDevices, setShowDevices] = useState(true);
    const {settings} = useGlobalSettings();
    const [isLoadingDevices, setIsLoadingDevices] = useState(false);
    const [selectedItem, setSelectedItem] = useState<string | null>(null);
    const [newName, setNewName] = useState<string>('');
    const [comment, setComment] = useState<string>('');
    const [showDeviceModal, setShowDeviceModal] = useState(false);

    const fetchInstallations = useCallback(async () => {
        setIsLoadingInstallations(true);
        try {
            const response = await fetch(`${settings.lynxUrl}/api/v2/installation`, {
                headers: {
                    'Accept': 'application/json',
                    'X-API-Key': token!
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch installations');
            }

            const installationsData = await response.json();
            setInstallations(installationsData);
        } catch (error) {
            console.error('Error fetching installations:', error);
            setInstallations([]);
        } finally {
            setIsLoadingInstallations(false);
        }
    }, [settings.lynxUrl, token]);

    const fetchDeviceFunctions = useCallback(async (deviceId: number, installationId: number): Promise<Functionx[]> => {
        try {
            const response = await fetch(`${settings.lynxUrl}/api/v2/functionx/${installationId}`, {
                headers: {
                    'Accept': 'application/json',
                    'X-API-Key': token!
                }
            });

            if (!response.ok) {
                console.error(`Failed to fetch functions for installation ${installationId}: ${response.status}`);
                return [];
            }

            const functionsData = await response.json();

            return functionsData.filter((func: Functionx) => {return Number(func.meta.device_id) === deviceId;});

        } catch (error) {
            console.error(`Error fetching functions for device ${deviceId}:`, error);
            return [];
        }
    }, [settings.lynxUrl, token]);

    const fetchDevices = useCallback(async (installationId: number) => {
        setIsLoadingDevices(true);
        try {
            const response = await fetch(`${settings.lynxUrl}/api/v2/devicex/${installationId}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-API-Key': token!
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Failed to fetch devices:', {
                    status: response.status,
                    statusText: response.statusText,
                    error: errorText
                });
                throw new Error(`Failed to fetch devices: ${response.status} ${response.statusText}`);
            }

            const devicesData: Devicex[] = await response.json();

            const deviceItems: ScannedItem[] = await Promise.all(
                devicesData.map(async device => {
                    const latitude = device.meta.latitude ? Number(device.meta.latitude) : null;
                    const longitude = device.meta.longitude ? Number(device.meta.longitude) : null;
                    const functions = await fetchDeviceFunctions(device.id, installationId);

                    return {...device,
                        name: device.meta.name,
                        content: device.type || (device.meta.zwave_specific as string) || 'Unknown Type',
                        latitude: latitude,
                        longitude: longitude,
                        functions: functions,
                        timestamp: Date.now(),
                    } as ScannedItem;
                })
            );

            setItems(deviceItems);
        } catch (error) {
            console.error('Error fetching devices:', error);
            setItems([]);
        } finally {
            setIsLoadingDevices(false);
        }
    }, [settings.lynxUrl, token, fetchDeviceFunctions]);

    useEffect(() => {
        if (token) {
            localStorage.setItem('auth_token', token);
            fetchInstallations();
        } else {
            localStorage.removeItem('auth_token');
            setInstallations([]);
            setIsLoadingInstallations(false);
            setSelectedInstallation(null);
            setItems([]);
        }
    }, [token, fetchInstallations]);

    useEffect(() => {
        if (selectedInstallation !== null && showDevices) {
            fetchDevices(selectedInstallation);
        }
    }, [selectedInstallation, showDevices, token, fetchDevices]);

    const handleLogin = (newToken: string) => {
        setToken(newToken);
        setCurrentPage('list');
        setShowDevices(true);
    };

    const handleLogout = () => {
        setToken(null);
        setItems([]);
    };

    const handleScan = async (result: string) => {
        if (!selectedInstallation) {
            alert('Please select an installation first');
            return;
        }

        if (!items.length) {
            try {
                const response = await fetch(`/api/installations/${selectedInstallation}/devices`);
                const data = await response.json();
                setItems(data);
            } catch (error) {
                console.error('Error fetching devices:', error);
                return;
            }
        }

        const matchingDevice = items.find(item => {
            const serialNr = item.meta?.serial_nr?.toLowerCase();
            const eui = item.meta?.eui?.toLowerCase();
            const scannedContent = result.toLowerCase();

            return serialNr === scannedContent || eui === scannedContent;
        });

        if (matchingDevice) {
            setCurrentPage('list');

            setTimeout(() => {
                setSelectedItem(matchingDevice.id.toString());
                setNewName(matchingDevice.name);
                setComment(matchingDevice.meta?.comment || '');
                setShowDeviceModal(true);

                const element = document.getElementById(`device-${matchingDevice.id}`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    element.classList.add('highlight-device');
                    setTimeout(() => {
                        element.classList.remove('highlight-device');
                    }, 2000);
                }
            }, 100);
        } else {
            alert('No device found with matching serial number or EUI.\nTry again.');
            setCurrentPage('list');
        }
    };

    const handleSelectInstallation = (installationId: number | null) => {
        setSelectedInstallation(installationId);
        setSelectedItem(null);
    };

    const handleUpdateLocation = (id: string, newLatitude: number | null, newLongitude: number | null) => {
        setItems((prevItems) =>
            prevItems.map((item) =>
                item.id === Number(id)
                    ? {
                        ...item,
                        latitude: newLatitude,
                        longitude: newLongitude,
                        timestamp: Date.now(),
                    }
                    : item
            )
        );
    };

    const handleUpdateName = (id: string, newName: string) => {
        setItems((prevItems) =>
            prevItems.map((item) =>
                item.id === Number(id)
                    ? {
                        ...item,
                        name: newName,
                    }
                    : item
            )
        );
    };

    if (!token) {
        return <Login onLogin={handleLogin} />;
    }

    return (
        <LynxProvider apiKey={token} apiURL={settings.lynxUrl}>
            <Container className="py-4" style={{ maxWidth: '540px' }}>
                <h1 className="text-center mb-4">Sensor installation</h1>

                <Row className="mb-4">
                    <Col className="d-flex justify-content-between align-items-center">
                        <InstallationSelect
                            value={selectedInstallation}
                            installations={installations}
                            isLoading={isLoadingInstallations}
                            onChange={handleSelectInstallation}
                        />
                        <Button
                            variant="outline-danger"
                            onClick={handleLogout}
                            className="ms-2"
                        >
                            <FontAwesomeIcon icon={faSignOutAlt} />
                        </Button>
                    </Col>
                </Row>

                {currentPage === 'scanner' && (
                    <Row className="mb-4">
                        <Col>
                            <Button
                                variant="outline-secondary"
                                onClick={() => setCurrentPage('list')}
                                className="mb-3"
                            >
                                Back to List
                            </Button>
                            <Scanner onScan={handleScan} />
                        </Col>
                    </Row>
                )}

                {currentPage === 'list' && (
                    <Row className="mb-4">
                        <Col>
                            <div className="d-flex gap-2 mb-3">
                                <Button
                                    variant="primary"
                                    onClick={() => setCurrentPage('scanner')}
                                    className="w-100"
                                    disabled={!selectedInstallation}
                                >
                                    <FontAwesomeIcon icon={faQrcode} className="me-2" />
                                    Scan Code
                                </Button>
                            </div>
                            {selectedInstallation && (
                                <div className="mt-2">
                                    <h6 className="mb-2">
                                        Devices in {installations.find(i => i.id === selectedInstallation)?.name}
                                    </h6>
                                    {isLoadingDevices ? (
                                        <p className="text-center text-primary">
                                            Loading devices...
                                        </p>
                                    ) : (
                                        <>
                                            <ItemList
                                                items={items}
                                                installationId={selectedInstallation}
                                                onUpdateLocation={handleUpdateLocation}
                                                onUpdateName={handleUpdateName}
                                                selectedItem={selectedItem}
                                                setSelectedItem={setSelectedItem}
                                                newName={newName}
                                                setNewName={setNewName}
                                                comment={comment}
                                                setComment={setComment}
                                                showDeviceModal={showDeviceModal}
                                                setShowDeviceModal={setShowDeviceModal}
                                            />
                                        </>
                                    )}
                                </div>
                            )}
                        </Col>
                    </Row>
                )}
            </Container>
        </LynxProvider>
    );
}

export default App;
